import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import axios from "axios";
import Cropper from "react-easy-crop";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionItem, AccordionContent, AccordionTrigger } from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { LoginForm } from "@/components/LoginForm";
import { PasswordChangeForm } from "@/components/PasswordChangeForm";
import { UserAdminPanel } from "@/components/UserAdminPanel";
import { MailServerSettings } from "@/components/MailServerSettings";
import ImportChildrenForm from "./components/ImportChildrenForm";
import ImportHistoryTable from "./components/ImportHistoryTable";
import RichTextEditor from "./components/RichTextEditor";
import UserPasswordAdminCard from "./components/UserPasswordAdminCard";
import { MailSignatureEditor } from "@/components/MailSignatureEditor";
import { MailTemplateManager } from "@/components/MailTemplateManager";
import { MailEditorSettingsCard } from "@/components/MailEditorSettingsCard";
import { EmailLogsCard } from "@/components/EmailLogsCard";
import { DemoTab } from "@/components/tabs/DemoTab";
import { ChildrenTabModern } from "@/components/tabs/ChildrenTabModern";
import { AttendanceTabModern } from "@/components/tabs/AttendanceTabModern";
import { StatsTabModern } from "@/components/tabs/StatsTabModern";
import { EvaluationTabModern } from "@/components/tabs/EvaluationTabModern";
import { EmailTabModern } from "@/components/tabs/EmailTabModern";
import { CourseSessionsTabModern } from "@/components/tabs/CourseSessionsTabModern";
import { CoursesTabModern } from "@/components/tabs/CoursesTabModern";
import { GroupsTabModern } from "@/components/tabs/GroupsTabModern";
import { UsersTabModern } from "@/components/tabs/UsersTabModern";
import { SystemSettingsTabModern } from "@/components/tabs/SystemSettingsTabModern";
import { TicketsTabModern } from "@/components/tabs/TicketsTabModern";
import { HelpTab } from "@/components/tabs/HelpTab";
import { ModernNavigation } from "@/components/ModernNavigation";
import { RolePermissionsMatrix } from "@/components/RolePermissionsMatrix";
import { ChildFormDialog } from "@/components/ChildFormDialog";
import { AppFooter } from "@/components/AppFooter";
import { TwoFactorVerification } from "@/components/TwoFactorVerification";
import { BackupCodesManager } from "@/components/BackupCodesManager";
import iconPresentActive from "@/assets/attendance/anwesend_aktiv.png";
import iconPresentInactive from "@/assets/attendance/anwesend_inaktiv.png";
import iconAbsentActive from "@/assets/attendance/fehlt_aktiv.png";
import iconAbsentInactive from "@/assets/attendance/fehlt_inaktiv.png";
import iconExcusedActive from "@/assets/attendance/entschuldigt_aktiv.png";
import iconExcusedInactive from "@/assets/attendance/entschuldigt_inaktiv.png";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Font-Settings beim App-Start laden und CSS-Variablen setzen
const applyFontSettings = (settings) => {
  if (!settings) return;
  
  const root = document.documentElement;
  
  // Google Font laden falls nicht Titillium Web
  const fonts = [settings.primaryFont, settings.headingFont].filter(f => f && f !== "Titillium Web");
  if (fonts.length > 0) {
    const uniqueFonts = [...new Set(fonts)];
    const fontLink = document.getElementById('dynamic-google-fonts');
    const fontUrl = `https://fonts.googleapis.com/css2?${uniqueFonts.map(f => `family=${f.replace(/ /g, '+')}:wght@300;400;500;600;700;800`).join('&')}&display=swap`;
    if (fontLink) {
      fontLink.href = fontUrl;
    } else {
      const link = document.createElement('link');
      link.id = 'dynamic-google-fonts';
      link.rel = 'stylesheet';
      link.href = fontUrl;
      document.head.appendChild(link);
    }
  }
  
  // CSS-Variablen setzen
  root.style.setProperty('--font-primary', settings.primaryFont || 'Titillium Web');
  root.style.setProperty('--font-heading', settings.headingFont || settings.primaryFont || 'Titillium Web');
  root.style.setProperty('--font-nav-size', `${settings.navigation?.size || 16}px`);
  root.style.setProperty('--font-nav-weight', settings.navigation?.weight || '700');
  root.style.setProperty('--font-h1-size', `${settings.h1?.size || 24}px`);
  root.style.setProperty('--font-h1-weight', settings.h1?.weight || '700');
  root.style.setProperty('--font-h2-size', `${settings.h2?.size || 18}px`);
  root.style.setProperty('--font-h2-weight', settings.h2?.weight || '600');
  root.style.setProperty('--font-h3-size', `${settings.h3?.size || 16}px`);
  root.style.setProperty('--font-h3-weight', settings.h3?.weight || '600');
  root.style.setProperty('--font-body-size', `${settings.body?.size || 14}px`);
  root.style.setProperty('--font-body-weight', settings.body?.weight || '500');
  root.style.setProperty('--font-names-size', `${settings.names?.size || 16}px`);
  root.style.setProperty('--font-names-weight', settings.names?.weight || '600');
  root.style.setProperty('--font-badge-size', `${settings.badges?.size || 14}px`);
  root.style.setProperty('--font-badge-weight', settings.badges?.weight || '600');
  root.style.setProperty('--font-label-size', `${settings.labels?.size || 14}px`);
  root.style.setProperty('--font-label-weight', settings.labels?.weight || '600');
  root.style.setProperty('--font-small-size', `${settings.small?.size || 12}px`);
  root.style.setProperty('--font-small-weight', settings.small?.weight || '500');
};

// Font-Settings initial laden (vor Login)
const loadFontSettings = async () => {
  try {
    const res = await axios.get(`${API_BASE}/api/font-settings`);
    applyFontSettings(res.data);
  } catch (e) {
    console.log('Font settings not available, using defaults');
  }
};
loadFontSettings();

// Navigation-Color-Settings initial laden (vor Login)
const loadNavigationSettings = async () => {
  try {
    const res = await axios.get(`${API_BASE}/api/navigation-settings`);
    const data = res.data;
    if (data.active_bg_color) {
      document.documentElement.style.setProperty('--nav-active-bg', data.active_bg_color);
    }
    if (data.active_text_color) {
      document.documentElement.style.setProperty('--nav-active-text', data.active_text_color);
    }
    if (data.hover_bg_color) {
      document.documentElement.style.setProperty('--nav-hover-bg', data.hover_bg_color);
    }
    if (data.hover_text_color) {
      document.documentElement.style.setProperty('--nav-hover-text', data.hover_text_color);
    }
  } catch (e) {
    console.log('Navigation settings not available, using defaults');
  }
};
loadNavigationSettings();

// Gruppen-Badge-Settings initial laden (vor Login)
const loadGroupBadgeSettings = async () => {
  try {
    const res = await axios.get(`${API_BASE}/api/group-color-presets`);
    const data = res.data;
    if (data.badge_settings) {
      document.documentElement.style.setProperty('--group-badge-font-size', `${data.badge_settings.font_size || 14}px`);
      document.documentElement.style.setProperty('--group-badge-font-weight', `${data.badge_settings.font_weight || 600}`);
      document.documentElement.style.setProperty('--group-badge-text-mode', data.badge_settings.text_color_mode || 'auto');
    }
    // Farben mit Textfarben im Window-Objekt speichern für späteren Zugriff
    if (data.colors) {
      window.__groupColorPresets = data.colors;
      window.__groupBadgeSettings = data.badge_settings;
    }
  } catch (e) {
    console.log('Group badge settings not available, using defaults');
  }
};
loadGroupBadgeSettings();

function App() {
  const { toast } = useToast();

  const [children, setChildren] = useState([]);
  const [archivedChildren, setArchivedChildren] = useState([]);
  const trainingCardRef = useRef(null);
  const profileCardRef = useRef(null);
  const [showArchived, setShowArchived] = useState(false);
  const [groupFilter, setGroupFilter] = useState("all");
  const [attendanceGroupFilter, setAttendanceGroupFilter] = useState("all");
  const [selectedChildForProfile, setSelectedChildForProfile] = useState(null);
  const [profileChildBeforeEdit, setProfileChildBeforeEdit] = useState(null);
  const [selectedChildForTraining, setSelectedChildForTraining] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedTab, setSelectedTab] = useState("children");
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true); // Loading-State für Gruppen
  const [imports, setImports] = useState([]);
  const [groupForm, setGroupForm] = useState({ id: null, name: "", trainer: "", weekday: "Sa.", start_time: "", end_time: "", color: "", order: "" });
  const [urlStateRestored, setUrlStateRestored] = useState(false); // Flag für URL-State-Wiederherstellung

  const [childForm, setChildForm] = useState({
    id: null,
    first_name: "",
    last_name: "",
    birth_date: "",
    phone: "",
    phone2: "",
    phone3: "",
    email: "",
    secondary_email: "",
    remark: "",
    notes: "",
    mnr: "",
    swim_start: "",
    gender: "",
    group_id: "",
    pause_until: "",
    membership_end_date: "",
  });
  const [childFormDirty, setChildFormDirty] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [savingChild, setSavingChild] = useState(false);

  const [showChildFormCard, setShowChildFormCard] = useState(false);

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [attendance, setAttendance] = useState({}); // { childId: { status, comment } }

  const [seriesStart, setSeriesStart] = useState("");
  const [seriesEnd, setSeriesEnd] = useState("");
  const [noEnd, setNoEnd] = useState(false);

  const [seriesError, setSeriesError] = useState("");

  const [statsChildId, setStatsChildId] = useState("");
  const [statsFrom, setStatsFrom] = useState("");
  const [statsTo, setStatsTo] = useState("");
  const [statsGroupFilter, setStatsGroupFilter] = useState("all");
  const [stats, setStats] = useState(null);
  const [groupStatsMatrix, setGroupStatsMatrix] = useState(null);
  const [childStatsMatrix, setChildStatsMatrix] = useState(null);
  const [showOnlyAttendedTerms, setShowOnlyAttendedTerms] = useState(false);
  const [groupMatrixPage, setGroupMatrixPage] = useState(0);


  const [deviceType, setDeviceType] = useState("desktop"); // "smartphone", "tablet", oder "desktop"
  const [deviceBreakpoints, setDeviceBreakpoints] = useState({ smartphone_max: 640, tablet_max: 1024 });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);



  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");

  const [trainerEmail, setTrainerEmail] = useState("");
  const [selectedEmailGroupIds, setSelectedEmailGroupIds] = useState([]);

  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [initialEmailBodyLoaded, setInitialEmailBodyLoaded] = useState(false);
  const [emailGroupFilterMode, setEmailGroupFilterMode] = useState("all"); // "all" | "only_new" | "exclude_new"

  const [mailTemplates, setMailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [mailEditorColors, setMailEditorColors] = useState(["#000000", "#1f2937", "#0f766e", "#2563eb", "#b91c1c"]);
  const [mailEditorFonts, setMailEditorFonts] = useState([
    'system-ui, -apple-system, "Segoe UI", sans-serif',
    "Arial, sans-serif",
    "Georgia, serif",
    "Tahoma, sans-serif",
    "Verdana, sans-serif",
  ]);
  const [mailEditorSizes, setMailEditorSizes] = useState({ standard: 14, h1: 26, h2: 22, h3: 18 });
  const [mailEditorStyles, setMailEditorStyles] = useState({ standard: "normal", h1: "bold", h2: "bold", h3: "bold" });

  const [settingsTab, setSettingsTab] = useState("account"); // "account" | "users" | "permissions" | "system"
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);

  const [authToken, setAuthToken] = useState("");
  const [currentUser, setCurrentUser] = useState(null); // { id, email, full_name, role }
  
  // 2FA State
  const [twoFactorPending, setTwoFactorPending] = useState(null); // { user_id, email }
  const [deviceId] = useState(() => {
    // Geräte-ID aus localStorage oder neu generieren
    let id = window.localStorage.getItem("device_id");
    if (!id) {
      id = 'dev_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      window.localStorage.setItem("device_id", id);
    }
    return id;
  });
  
  // Auto-Logout Einstellungen
  const [sessionSettings, setSessionSettings] = useState({ auto_logout_enabled: true, auto_logout_minutes: 30 });
  const lastActivityRef = useRef(Date.now());
  
  // Ref für EmailLogsCard um nach E-Mail-Versand die Protokolle zu aktualisieren
  const emailLogsRef = useRef(null);
  
  // Ref für permanenten File-Input (wichtig für mobile Geräte)
  const photoInputRef = useRef(null);
  const pendingPhotoChildIdRef = useRef(null);
  const pendingPhotoCallbackRef = useRef(null);

  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropForChildId, setCropForChildId] = useState(null);
  const [cropOnSavedCallback, setCropOnSavedCallback] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const isLoggedIn = !!authToken;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && error.response.status === 401) {
        // Session abgelaufen oder nicht mehr gültig -> automatisch ausloggen
        setAuthToken("");
        setCurrentUser(null);
        setForcePasswordChange(false);
        window.localStorage.removeItem("auth_token");
        toast({
          title: "Sitzung abgelaufen",
          description: "Bitte melde dich erneut an.",
          variant: "destructive",
        });
      }
      return Promise.reject(error);
    },
  );

  // Handler für Foto-Auswahl (verwendet permanenten Input)
  const handlePhotoInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file && pendingPhotoChildIdRef.current) {
      // Cropper-Dialog öffnen
      setCropForChildId(pendingPhotoChildIdRef.current);
      setCropImageSrc(URL.createObjectURL(file));
      setCropDialogOpen(true);
      // Callback speichern für späteren Aufruf nach erfolgreichem Upload
      if (pendingPhotoCallbackRef.current) {
        setCropOnSavedCallback(() => pendingPhotoCallbackRef.current);
      }
    }
    // Input zurücksetzen für erneute Auswahl desselben Fotos
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  }, []);

  // Funktion zum Öffnen des Foto-Dialogs
  const triggerPhotoUpload = useCallback((childId, onSavedCallback) => {
    pendingPhotoChildIdRef.current = childId;
    pendingPhotoCallbackRef.current = onSavedCallback;
    if (photoInputRef.current) {
      photoInputRef.current.click();
    }
  }, []);

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleConfirmCrop = useCallback(async () => {
    if (!cropImageSrc || !croppedAreaPixels || !cropForChildId) return;
    try {
      const image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = cropImageSrc;
      });
      const canvas = document.createElement("canvas");
      const targetSize = 300; // Profilbilder in 300x300 Pixel
      canvas.width = targetSize;
      canvas.height = targetSize;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Kein Canvas-Kontext verfügbar");

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      const sx = croppedAreaPixels.x * scaleX;
      const sy = croppedAreaPixels.y * scaleY;
      const sWidth = croppedAreaPixels.width * scaleX;
      const sHeight = croppedAreaPixels.height * scaleY;

      ctx.drawImage(image, sx, sy, sWidth, sHeight, 0, 0, targetSize, targetSize);

      await new Promise((resolve, reject) => {
        canvas.toBlob(
          async (blob) => {
            if (!blob) {
              reject(new Error("Konnte Bild nicht konvertieren"));
              return;
            }
            try {
              const response = await uploadChildPhotoBlob(cropForChildId, blob);
              toast({ title: "Foto gespeichert" });
              
              // Kind-Objekt aus Response verwenden für State-Update
              if (response?.data) {
                handleChildUpdate(response.data);
                // Callback für lokalen State in ChildrenTabModern aufrufen
                if (cropOnSavedCallback) {
                  cropOnSavedCallback(response.data);
                }
              }
            } catch (err) {
              console.error(err);
              toast({
                title: "Fehler beim Hochladen des Fotos",
                description: "Bitte später erneut versuchen.",
                variant: "destructive",
              });
            }
            resolve();
          },
          "image/jpeg",
          0.6, // Komprimierung: 60% Qualität (statt 90%) - spart ~50% Speicher bei 300x300px
        );
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Fehler beim Zuschneiden des Fotos",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    } finally {
      // Cleanup - alle States zurücksetzen
      if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
      setCropImageSrc(null);
      setCropForChildId(null);
      setCroppedAreaPixels(null);
      setCropDialogOpen(false);
      setCropOnSavedCallback(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  }, [cropImageSrc, croppedAreaPixels, cropForChildId, cropOnSavedCallback]);
  const isSuperAdmin = currentUser?.role === "superadmin";
  const isAdmin = currentUser?.role === "admin";
  const isMitarbeiter = currentUser?.role === "mitarbeiter";
  const isTrainer = currentUser?.role === "trainer";
  const canManageChildPhotos = isSuperAdmin || isAdmin || isTrainer;

  const sortedChildren = useMemo(
    () =>
      [...children].sort((a, b) =>
        a.first_name.localeCompare(b.first_name, "de", { sensitivity: "base" }),
      ),
    [children],
  );
  const sortedSessions = useMemo(
    () =>
      [...sessions].sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0)),
    [sessions],
  );


  const statsChildren = useMemo(
    () =>
      statsGroupFilter === "all"
        ? sortedChildren.filter((c) => !c.archived)
        : sortedChildren.filter((c) => c.group_id === statsGroupFilter && !c.archived),
    [sortedChildren, statsGroupFilter],
  );

  // Breakpoints beim Start laden
  useEffect(() => {
    const loadBreakpoints = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/device-breakpoints`);
        if (res.data) {
          setDeviceBreakpoints(res.data);
        }
      } catch (e) {
        console.log('Device breakpoints not available, using defaults');
      }
    };
    loadBreakpoints();
  }, []);

  useEffect(() => {
    const checkDevice = () => {
      if (typeof window !== "undefined") {
        const width = window.innerWidth;
        const ua = navigator.userAgent || navigator.vendor || "";
        const isPhoneUA = /iPhone|Android.+Mobile/.test(ua);
        
        // Gerätetyp basierend auf Breakpoints bestimmen
        if (width <= deviceBreakpoints.smartphone_max || isPhoneUA) {
          setDeviceType("smartphone");
        } else if (width <= deviceBreakpoints.tablet_max) {
          setDeviceType("tablet");
        } else {
          setDeviceType("desktop");
        }
      }
    };

    const handleScroll = () => {
      if (typeof window !== "undefined") {
        setShowScrollTop(window.scrollY > 400);
      }
    };

    checkDevice();
    handleScroll();
    window.addEventListener("resize", checkDevice);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", checkDevice);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [deviceBreakpoints]);





  const selectedSession = useMemo(
    () => sortedSessions.find((s) => s.id === selectedSessionId) || null,
    [sortedSessions, selectedSessionId],
  );

  const todayISO = new Date().toISOString().slice(0, 10);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const canEditAttendance = selectedSession
    ? !isMitarbeiter || selectedSession.date === todayISO
    : false;

  const selectedStatsChild = useMemo(
    () => sortedChildren.find((c) => c.id === statsChildId) || null,
    [sortedChildren, statsChildId],
  );


  const filteredChildren = useMemo(
    () =>
      groupFilter === "all"
        ? sortedChildren
        : sortedChildren.filter((c) => c.group_id === groupFilter),
    [sortedChildren, groupFilter],
  );

  const currentTrainingIndex = useMemo(
    () =>
      selectedChildForTraining
        ? filteredChildren.findIndex((c) => c.id === selectedChildForTraining.id)
        : -1,
    [filteredChildren, selectedChildForTraining],
  );

  const hasPrevTrainingChild = currentTrainingIndex > 0;
  const hasNextTrainingChild =
    currentTrainingIndex >= 0 && currentTrainingIndex < filteredChildren.length - 1;

  const attendanceChildren = useMemo(
    () =>
      attendanceGroupFilter === "all"
        ? sortedChildren
        : attendanceGroupFilter
        ? sortedChildren.filter((c) => c.group_id === attendanceGroupFilter)
        : sortedChildren,
    [sortedChildren, attendanceGroupFilter],
  );
  // Scroll-to-top State & Listener (bereits weiter oben definiert)


  const attendancePresentCounts = useMemo(() => {
    let total = 0;
    const byGroup = {};
    sortedChildren.forEach((c) => {
      const status = attendance[c.id]?.status;
      if (status === "anwesend") {
        total += 1;
        if (c.group_id) {
          byGroup[c.group_id] = (byGroup[c.group_id] || 0) + 1;
        }
      }
    });
    return { total, byGroup };
  }, [sortedChildren, attendance]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === groupFilter) || null,
    [groups, groupFilter],
  );

  const selectedGroupChildrenCount = useMemo(
    () =>
      groupFilter !== "all" && groupFilter !== "archived"
        ? sortedChildren.filter((c) => c.group_id === groupFilter).length
        : 0,
    [sortedChildren, groupFilter],
  );

 

  function authHeaders() {
    return authToken
      ? {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      : {};
  }

  async function uploadChildPhotoBlob(childId, blob) {
    const formData = new FormData();
    formData.append("file", blob, "profilfoto.jpg");
    const response = await axios.post(`${API_BASE}/api/children/${childId}/photo`, formData, {
      headers: {
        ...(authHeaders()?.headers || {}),
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  }

  function createCroppedImageBlob(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const size = Math.min(img.width, img.height);
          const sx = (img.width - size) / 2;
          const sy = (img.height - size) / 2;
          const canvas = document.createElement("canvas");
          const targetSize = 300; // Profilbilder in 300x300 Pixel
          canvas.width = targetSize;
          canvas.height = targetSize;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Kein Canvas-Kontext verfügbar"));
            return;
          }
          ctx.drawImage(img, sx, sy, size, size, 0, 0, targetSize, targetSize);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Konnte Bild nicht konvertieren"));
                return;
              }
              resolve(blob);
            },
            "image/jpeg",
            0.6, // Komprimierung: 60% Qualität - spart ~50% Speicher
          );
        } catch (err) {
          reject(err);
        } finally {
          URL.revokeObjectURL(img.src);
        }
      };
      img.onerror = (err) => reject(err);
      img.src = URL.createObjectURL(file);
    });
  }

  async function uploadCroppedChildPhoto(childId, file) {
    const croppedBlob = await createCroppedImageBlob(file);
    await uploadChildPhotoBlob(childId, croppedBlob);
  }

  async function fetchChildren() {
    try {
      const res = await axios.get(`${API_BASE}/api/children`, authHeaders());
      let data = res.data || [];
      // Trainer sieht nur Kinder, für die er Gruppenrechte hat
      if (currentUser?.role === "trainer" && Array.isArray(currentUser.allowed_group_ids) && currentUser.allowed_group_ids.length > 0) {
        data = data.filter((c) => c.group_id && currentUser.allowed_group_ids.includes(c.group_id));
      }
      setChildren(data);
      // Archivierte Kinder immer laden für den Archiv-Tab
      try {
        const arch = await axios.get(`${API_BASE}/api/children/archived/list`, authHeaders());
        setArchivedChildren(arch.data || []);
      } catch (archErr) {
        console.error("Error loading archived children:", archErr);
        setArchivedChildren([]);
      }
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Laden der Kinder",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }

  async function fetchSessions() {
    try {
      const res = await axios.get(`${API_BASE}/api/sessions`, authHeaders());
      setSessions(res.data);
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Laden der Trainingstermine",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }
  async function fetchImports() {
    try {
      const res = await axios.get(`${API_BASE}/api/import/children`, authHeaders());
      setImports(res.data || []);
    } catch (e) {
      console.error(e);
      // Fehler hier nur loggen, Import-Tab zeigt ggf. leere Tabelle
    }
  }


  async function updateTrainingSeepferdchen(childId, changes) {
    try {
      const res = await axios.post(
        `${API_BASE}/api/children/${childId}/training-seepferdchen`,
        changes,
        authHeaders(),
      );
      const updatedChild = res.data;
      setChildren((prev) => prev.map((c) => (c.id === updatedChild.id ? updatedChild : c)));
      setArchivedChildren((prev) => prev.map((c) => (c.id === updatedChild.id ? updatedChild : c)));
      setSelectedChildForTraining((prev) => (prev && prev.id === updatedChild.id ? updatedChild : prev));
      setSelectedChildForProfile((prev) => (prev && prev.id === updatedChild.id ? updatedChild : prev));
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Speichern des Seepferdchen-Status",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }

  async function updateTrainingFreischwimmer(childId, changes) {
    try {
      const res = await axios.post(
        `${API_BASE}/api/children/${childId}/training-freischwimmer`,
        changes,
        authHeaders(),
      );
      const updatedChild = res.data;
      setChildren((prev) => prev.map((c) => (c.id === updatedChild.id ? updatedChild : c)));
      setArchivedChildren((prev) => prev.map((c) => (c.id === updatedChild.id ? updatedChild : c)));
      setSelectedChildForTraining((prev) => (prev && prev.id === updatedChild.id ? updatedChild : prev));
      setSelectedChildForProfile((prev) => (prev && prev.id === updatedChild.id ? updatedChild : prev));
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Speichern des Freischwimmer-Status",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }

  async function updateTrainingLevel(childId, level) {
    try {
      const res = await axios.post(
        `${API_BASE}/api/children/${childId}/training-level`,
        { level },
        authHeaders(),
      );
      const updatedChild = res.data;
      setChildren((prev) => prev.map((c) => (c.id === updatedChild.id ? updatedChild : c)));
      setArchivedChildren((prev) => prev.map((c) => (c.id === updatedChild.id ? updatedChild : c)));
      setSelectedChildForTraining((prev) => (prev && prev.id === updatedChild.id ? updatedChild : prev));
      setSelectedChildForProfile((prev) => (prev && prev.id === updatedChild.id ? updatedChild : prev));
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Speichern des Levels",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }

  // Generische Funktion zum Aktualisieren eines Kindes im State
  function handleChildUpdate(updatedChild) {
    if (!updatedChild || !updatedChild.id) return;
    setChildren((prev) => prev.map((c) => (c.id === updatedChild.id ? updatedChild : c)));
    setArchivedChildren((prev) => prev.map((c) => (c.id === updatedChild.id ? updatedChild : c)));
    setSelectedChildForTraining((prev) => (prev && prev.id === updatedChild.id ? updatedChild : prev));
    setSelectedChildForProfile((prev) => (prev && prev.id === updatedChild.id ? updatedChild : prev));
  }

  async function fetchAttendanceForSession(sessionId, groupId) {
    if (!sessionId) {
      setAttendance({});
      return;
    }
    try {
      // Immer alle Anwesenheitsdaten für den ausgewählten Kurstermin laden.
      // Die Filterung nach Gruppe erfolgt ausschließlich im Frontend über attendanceGroupFilter.
      const params = { session_id: sessionId };
      const res = await axios.get(`${API_BASE}/api/attendance`, {
        params,
        ...authHeaders(),
      });
      const map = {};
      res.data.forEach((a) => {
        map[a.child_id] = { status: a.status, comment: a.comment || "" };
      });
      setAttendance(map);
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Laden der Anwesenheit",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }

  async function loadMailTemplates() {
    if (!authToken) return;
    try {
      const res = await axios.get(`${API_BASE}/api/mail-templates`, authHeaders());
      setMailTemplates(res.data || []);
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    const storedToken = window.localStorage.getItem("auth_token");
    if (storedToken) {
      setAuthToken(storedToken);
      const storedUser = window.localStorage.getItem("current_user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setCurrentUser(parsedUser);
          if (parsedUser.must_change_password) {
            setForcePasswordChange(true);
          }
        } catch {
          // ignorieren
        }
      }
      
      // Aktuelle Benutzerdaten inkl. Berechtigungen vom Server laden
      (async () => {
        try {
          const res = await axios.get(`${API_BASE}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          const freshUser = res.data;
          setCurrentUser(freshUser);
          // LocalStorage aktualisieren
          window.localStorage.setItem("current_user", JSON.stringify(freshUser));
          if (freshUser.must_change_password) {
            setForcePasswordChange(true);
          }
        } catch (err) {
          // Token ungültig - ausloggen
          if (err?.response?.status === 401) {
            window.localStorage.removeItem("auth_token");
            window.localStorage.removeItem("current_user");
            setAuthToken("");
            setCurrentUser(null);
          }
        }
      })();
    }

    (async () => {
      const storedTrainer = window.localStorage.getItem("trainer_email");
      if (storedTrainer) {
        setTrainerEmail(storedTrainer);
      }

      // E-Mail-Editor beim Laden immer mit leeren Feldern starten
      setEmailSubject("");
      setEmailBody("");
      window.localStorage.removeItem("email_subject");
      window.localStorage.removeItem("email_body");
    })();
  }, []);

  useEffect(() => {
    if (!authToken) return;
    (async () => {
      try {
        const settingsRes = await axios.get(`${API_BASE}/api/mail-editor-settings`, authHeaders());
        if (Array.isArray(settingsRes.data?.colors)) {
          setMailEditorColors(settingsRes.data.colors.slice(0, 5));
        }
        if (Array.isArray(settingsRes.data?.fonts)) {
          setMailEditorFonts(settingsRes.data.fonts.slice(0, 5));
        }
        if (settingsRes.data?.sizes) {
          setMailEditorSizes(settingsRes.data.sizes);
        }
        if (settingsRes.data?.styles) {
          setMailEditorStyles(settingsRes.data.styles);
        }
        if (typeof settingsRes.data?.link_color === "string") {
          document.documentElement.style.setProperty("--mail-link-color", settingsRes.data.link_color);
        } else {
          document.documentElement.style.removeProperty("--mail-link-color");
        }
      } catch (e) {
        console.error(e);
      }
    })();

    // Paralleles Laden aller Daten für bessere Performance
    (async () => {
      await Promise.all([
        fetchChildren(),
        fetchSessions(),
        fetchGroups(),
        fetchImports(),
      ]);
      loadMailTemplates();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // URL-Parameter beim Laden der Seite wiederherstellen
  useEffect(() => {
    if (!authToken || urlStateRestored) return;
    
    // Warte bis Kinder geladen sind (nur wenn URL Kind-Parameter hat)
    const params = new URLSearchParams(window.location.search);
    const childParam = params.get('kind');
    
    if (childParam && children.length === 0) {
      // Warte auf Kinder-Daten
      return;
    }
    
    const tabParam = params.get('tab');
    const groupParam = params.get('gruppe');
    
    // Tab wiederherstellen
    if (tabParam) {
      setSelectedTab(tabParam);
    }
    
    // Gruppe wiederherstellen
    if (groupParam) {
      setGroupFilter(groupParam);
    }
    
    // Kind-Profil wiederherstellen
    if (childParam && children.length > 0) {
      const child = children.find(c => c.id === childParam);
      if (child) {
        setSelectedChildForProfile(child);
        if (!groupParam) {
          setGroupFilter(child.group_id || "all");
        }
      }
    }
    
    setUrlStateRestored(true);
  }, [authToken, children, urlStateRestored]);

  // URL aktualisieren wenn sich Tab, Kind oder Gruppe ändert
  useEffect(() => {
    if (!authToken || !urlStateRestored) return;
    
    const params = new URLSearchParams();
    
    if (selectedTab && selectedTab !== "children") {
      params.set('tab', selectedTab);
    }
    
    if (selectedChildForProfile) {
      params.set('kind', selectedChildForProfile.id);
      // Tab immer auf 'children' setzen wenn Kind ausgewählt
      params.set('tab', 'children');
    }
    
    if (groupFilter && groupFilter !== "all" && !selectedChildForProfile) {
      params.set('gruppe', groupFilter);
    }
    
    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    
    window.history.replaceState({}, '', newUrl);
  }, [authToken, urlStateRestored, selectedTab, selectedChildForProfile, groupFilter]);

  useEffect(() => {
    if (sessions.length === 0) {
      if (selectedSessionId) setSelectedSessionId("");
      return;
    }
    if (!selectedSessionId) {
      const todayISO = new Date().toISOString().slice(0, 10);
      const sorted = [...sessions]
        .filter((s) => s.active !== false)
        .sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0));
      const futureOrToday = sorted.filter((s) => s.date >= todayISO);
      const target = futureOrToday.length > 0 ? futureOrToday[0] : sorted[sorted.length - 1];
      if (target) setSelectedSessionId(target.id);
    }
  }, [sessions, selectedSessionId]);
  useEffect(() => {
    if (!authToken) return;
    if (statsGroupFilter !== "all") {
      (async () => {
        await handleLoadStats();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsGroupFilter]);



  useEffect(() => {
    if (selectedSessionId && attendanceGroupFilter) {
      (async () => {
        await fetchAttendanceForSession(selectedSessionId, attendanceGroupFilter);
      })();
    } else {
      setAttendance({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionId, attendanceGroupFilter]);

  function resetChildForm() {
    setChildForm({
      id: null,
      first_name: "",
      last_name: "",
      birth_date: "",
      phone: "",
      phone2: "",
      phone3: "",
      email: "",
      secondary_email: "",
      remark: "",
      notes: "",
      mnr: "",
      swim_start: "",
      gender: "",
      group_id: "",
      pause_until: "",
      membership_end_date: "",
    });
    setChildFormDirty(false);
    setSelectedChildForProfile(null);
    setShowChildFormCard(false);
  }

  function startCreateNewChild() {
    setSelectedChildForTraining(null);
    setSelectedChildForProfile(null);
    resetChildForm();
    setChildFormDirty(false);
    setShowChildFormCard(true);
  }

  const handleDownloadOfflineAttendancePdf = (overrideGroupId = null) => {
    const targetGroupId = overrideGroupId || attendanceGroupFilter;
    if (!targetGroupId || targetGroupId === "all") return;
    const group = groups.find((g) => g.id === targetGroupId);
    if (!group) return;

    // Filter children for the target group
    const groupChildren = (children || []).filter((c) => c.group_id === targetGroupId && c.status !== "archiviert");

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Header
    const title = `Anwesenheitsliste – ${group.name}`;
    const timeText = group.start_time && group.end_time ? `${group.start_time} – ${group.end_time} Uhr` : "";
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(title, 10, 15);
    if (timeText) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(timeText, 10, 22);
    }
    const dateText = new Date().toLocaleDateString("de-DE");
    doc.setFontSize(10);
    doc.text(`Erstellt am ${dateText}`, pageWidth - 10, 10, { align: "right" });

    // Tabelle
    const marginLeft = 10;
    const marginRight = 10;
    const marginTopTable = 30;
    const marginBottom = 10;

    const availableWidth = pageWidth - marginLeft - marginRight;
    const nameColWidth = Math.max(40, Math.min(60, availableWidth * 0.25));
    const columns = 10; // 10 leere Spalten fcr Termine
    const colWidth = (availableWidth - nameColWidth) / columns;

    const rows = groupChildren.length || 15; // Fallback, falls noch keine Kinder geladen
    const availableHeight = pageHeight - marginTopTable - marginBottom;
    const rowHeight = availableHeight / (rows + 1); // +1 fcr Header-Zeile

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");

    // Header-Zeile
    let x = marginLeft;
    let y = marginTopTable;
    doc.rect(x, y, nameColWidth, rowHeight);
    doc.text("Name", x + 2, y + rowHeight / 2 + 2);
    x += nameColWidth;
    for (let i = 0; i < columns; i++) {
      doc.rect(x, y, colWidth, rowHeight);
      // Kopfzellen bewusst leer lassen – Termine werden von Hand eingetragen
      x += colWidth;
    }

    // Kinder-Zeilen
    doc.setFont("helvetica", "normal");
    groupChildren.forEach((child, index) => {
      const rowY = marginTopTable + rowHeight * (index + 1);
      let cellX = marginLeft;
      doc.rect(cellX, rowY, nameColWidth, rowHeight);
      const fullName = `${child.first_name} ${child.last_name}`;
      doc.text(fullName, cellX + 2, rowY + rowHeight / 2 + 2);
      cellX += nameColWidth;
      for (let i = 0; i < columns; i++) {
        doc.rect(cellX, rowY, colWidth, rowHeight);
        cellX += colWidth;
      }
    });

    doc.save(`Anwesenheit_${group.name}.pdf`);
  };

  function handleChildEdit(child) {
    // Wenn child null ist, öffnen wir das Formular für ein neues Kind
    if (!child) {
      setChildForm({
        id: null,
        first_name: "",
        last_name: "",
        birth_date: "",
        phone: "",
        phone2: "",
        phone3: "",
        email: "",
        secondary_email: "",
        remark: "",
        notes: "",
        mnr: "",
        swim_start: "",
        gender: "",
        group_id: "",
        pause_until: "",
        membership_end_date: "",
      });
      setChildFormDirty(false);
      setShowChildFormCard(true);
      return;
    }
    
    setChildForm({
      id: child.id,
      first_name: child.first_name,
      last_name: child.last_name,
      birth_date: child.birth_date ? formatDateISOToGerman(child.birth_date) : "",
      phone: child.phone || "",
      phone2: child.phone2 || "",
      phone3: child.phone3 || "",
      email: child.email || "",
      secondary_email: child.secondary_email || "",
      remark: child.remark || "",
      notes: child.notes || "",
      mnr: child.mnr || "",
      swim_start: child.swim_start || "",
      gender: child.gender || "",
      group_id: child.group_id || "",
      pause_until: child.pause_until || "",
      membership_end_date: child.membership_end_date || "",
    });
    setChildFormDirty(false);
    setShowChildFormCard(true);
  }

  function handleOpenProfile(child) {
    // Original-Kind für Profilansicht merken, damit wir nach Abbrechen wieder zur Profilansicht zurückkehren können
    setProfileChildBeforeEdit(child);
    handleChildEdit(child);
    setSelectedChildForProfile(child);
  }

  async function handleChildSubmit(e) {
    e.preventDefault();

    let isoBirth = null;
    if (childForm.birth_date) {
      const raw = childForm.birth_date.trim();
      const mDe = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      const mIso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (mDe) {
        const [_, dd, mm, yyyy] = mDe;
        isoBirth = `${yyyy}-${mm}-${dd}`;
      } else if (mIso) {
        isoBirth = raw;
      } else {
        isoBirth = raw;
      }
    }

    let isoPauseUntil = null;
    if (childForm.pause_until) {
      const raw = childForm.pause_until.trim();
      const mDe = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      const mIso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (mDe) {
        const [_, dd, mm, yyyy] = mDe;
        isoPauseUntil = `${yyyy}-${mm}-${dd}`;
      } else if (mIso) {
        isoPauseUntil = raw;
      } else {
        isoPauseUntil = raw;
      }
    }

    const payload = {
      first_name: childForm.first_name,
      last_name: childForm.last_name,
      birth_date: isoBirth,
      phone: childForm.phone || null,
      phone2: childForm.phone2 || null,
      phone3: childForm.phone3 || null,
      email: childForm.email || null,
      secondary_email: childForm.secondary_email || null,
      remark: childForm.remark || null,
      notes: childForm.notes || null,
      mnr: childForm.mnr || null,
      swim_start: childForm.swim_start || null,
      gender: childForm.gender || null,
      group_id: childForm.group_id || null,
      pause_until: isoPauseUntil,
      membership_end_date: childForm.membership_end_date || null,
    };
    try {
      if (childForm.id) {
        await axios.put(`${API_BASE}/api/children/${childForm.id}`, payload, authHeaders());
        toast({ title: "Kind aktualisiert" });
        setShowChildFormCard(false);
        setChildFormDirty(false);
        setSelectedChildForProfile((prev) => {
          if (prev && prev.id === childForm.id) {
            return { ...prev, ...payload, id: childForm.id };
          }
          const base = children.find((c) => c.id === childForm.id) || {};
          return { ...base, ...payload, id: childForm.id };
        });
      } else {
        await axios.post(`${API_BASE}/api/children`, payload, authHeaders());
        toast({ title: "Kind angelegt" });
        resetChildForm();
      }
      fetchChildren();
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Speichern des Kindes",
        description: "Bitte überprüfe die Eingaben.",
        variant: "destructive",
      });
    }
  }

  async function handleArchiveChild(id) {
    if (!window.confirm("Dieses Profil wirklich archivieren?")) return;
    try {
      await axios.post(`${API_BASE}/api/children/${id}/archive`, null, authHeaders());
      toast({ title: "Profil archiviert" });
      fetchChildren();
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Archivieren des Profils",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }

  async function handlePromoteChildGroup(id) {
    try {
      const res = await axios.post(`${API_BASE}/api/children/${id}/promote-group`, null, authHeaders());
      toast({
        title: "Gruppe vorwärts gewechselt",
        description: res.data.group_name
          ? `Kind ist jetzt in der Gruppe "${res.data.group_name}".`
          : "Gruppenzuordnung aktualisiert.",
      });
      fetchChildren();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Der Gruppenwechsel konnte nicht durchgeführt werden.";
      toast({ title: "Fehler beim Gruppenwechsel", description: msg, variant: "destructive" });
    }
  }

  async function handleDemoteChildGroup(id) {
    try {
      const res = await axios.post(`${API_BASE}/api/children/${id}/demote-group`, null, authHeaders());
      toast({
        title: "Gruppe zurück gewechselt",
        description: res.data.group_name
          ? `Kind ist jetzt in der Gruppe "${res.data.group_name}".`
          : "Gruppenzuordnung aktualisiert.",
      });
      fetchChildren();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Der Gruppenwechsel konnte nicht durchgeführt werden.";
      toast({ title: "Fehler beim Gruppenwechsel", description: msg, variant: "destructive" });
    }
  }

  async function handleChangeChildGroup(id, targetGroupId) {
    try {
      const res = await axios.post(`${API_BASE}/api/children/${id}/change-group`, { group_id: targetGroupId }, authHeaders());
      toast({
        title: "Gruppenwechsel erfolgreich",
        description: res.data.group_name
          ? `Kind ist jetzt in der Gruppe "${res.data.group_name}".`
          : "Gruppenzuordnung aktualisiert.",
      });
      fetchChildren();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Der Gruppenwechsel konnte nicht durchgeführt werden.";
      toast({ title: "Fehler beim Gruppenwechsel", description: msg, variant: "destructive" });
    }
  }

  async function handleRestoreChild(id) {
    try {
      await axios.post(`${API_BASE}/api/children/${id}/restore`, null, authHeaders());
      toast({ title: "Profil wiederhergestellt" });
      const arch = await axios.get(`${API_BASE}/api/children/archived/list`, authHeaders());
      setArchivedChildren(arch.data);
      fetchChildren();
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Wiederherstellen des Profils",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }

  async function handleDeleteArchivedChild(id, firstName, lastName) {
    if (!window.confirm(`Soll das archivierte Profil von ${firstName} ${lastName} endgültig gelöscht werden?`)) {
      return;
    }
    try {
      await axios.delete(`${API_BASE}/api/children/${id}/archived`, authHeaders());
      toast({ title: "Archiviertes Profil gelöscht" });
      const arch = await axios.get(`${API_BASE}/api/children/archived/list`, authHeaders());
      setArchivedChildren(arch.data);
      fetchChildren();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Das archivierte Profil konnte nicht gelöscht werden.";
      toast({ title: "Fehler beim Löschen", description: msg, variant: "destructive" });
    }
  }

  async function handleCreateSessionForDate() {
    if (!selectedDate) {
      toast({
        title: "Bitte Datum wählen",
        variant: "destructive",
      });
      return;
    }
    try {
      const res = await axios.post(
        `${API_BASE}/api/sessions`,
        {
          date: selectedDate,
        },
        authHeaders(),
      );
      toast({ title: "Trainingstermin angelegt" });
      setSelectedSessionId(res.data.id);
      fetchSessions();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail ||
        "Der Termin konnte nicht angelegt werden. Stelle sicher, dass das Datum ein Samstag ist.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    }
  }

  async function handleGenerateWeeklySeries() {
    setSeriesError("");

    if (!seriesStart) {
      setSeriesError("Bitte Startdatum eingeben.");
      toast({
        title: "Bitte Startdatum eingeben",
        variant: "destructive",
      });
      return;
    }

    if (!seriesEnd && !noEnd) {
      setSeriesError("Bitte Enddatum eingeben oder 'ohne Ende' auswählen.");
      toast({
        title: "Bitte Enddatum eingeben oder 'ohne Ende' auswählen",
        variant: "destructive",
      });
      return;
    }

    const start = new Date(seriesStart);
    const end = seriesEnd ? new Date(seriesEnd) : new Date(start.getTime() + 365 * 24 * 60 * 60 * 1000);

    if (start > end) {
      setSeriesError("Startdatum darf nicht nach dem Enddatum liegen.");
      toast({ title: "Fehler", description: "Startdatum darf nicht nach dem Enddatum liegen.", variant: "destructive" });
      return;
    }

    // Prüfen, ob im Zeitraum überhaupt ein Samstag enthalten ist
    const hasSaturday = (() => {
      const d = new Date(start);
      while (d <= end) {
        if (d.getDay() === 6) return true; // 6 = Samstag
        d.setDate(d.getDate() + 1);
      }
      return false;
    })();

    if (!hasSaturday) {
      setSeriesError("Im gewählten Zeitraum liegt kein Samstag. Bitte Zeitraum anpassen.");
      toast({ title: "Kein Samstag im Zeitraum", description: "Bitte wähle einen Zeitraum mit mindestens einem Samstag.", variant: "destructive" });
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/api/sessions/generate`,
        {
          start_date: seriesStart,
          end_date: noEnd ? null : seriesEnd,
          no_end: noEnd,
        },
        authHeaders(),
      );
      toast({
        title: "Serientermine angelegt",
        description: `${res.data.created} Samstag-Termine wurden neu erstellt.`,
      });
      fetchSessions();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Die Serientermine konnten nicht angelegt werden.";
      setSeriesError(msg);
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    }
  }
  async function saveSingleAttendance(childId, override) {
    if (!selectedSessionId) {
      toast({
        title: "Bitte zuerst einen Kurstermin (Samstag) wählen.",
        variant: "destructive",
      });
      return;
    }

    const isAllGroups = attendanceGroupFilter === "all";
    const canUseAllGroups = isSuperAdmin || isAdmin;

    if (!attendanceGroupFilter || (isAllGroups && !canUseAllGroups)) {
      toast({
        title: "Bitte zuerst eine konkrete Gruppe für die Anwesenheit auswählen.",
        variant: "destructive",
      });
      return;
    }

    const effectiveGroupId = isAllGroups ? null : attendanceGroupFilter;

    const current = attendance[childId] || {};
    const status = override?.status ?? current.status ?? "fehlt";
    const comment = override?.comment ?? current.comment ?? null;

    try {
      await axios.post(
        `${API_BASE}/api/attendance/bulk`,
        {
          session_id: selectedSessionId,
          group_id: effectiveGroupId,
          entries: [
            {
              child_id: childId,
              status,
              comment,
            },
          ],
        },
        authHeaders(),
      );
      toast({ title: "Anwesenheit gespeichert" });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Die Anwesenheit konnte nicht gespeichert werden.";
      toast({
        title: "Fehler beim Speichern der Anwesenheit",
        description: msg,
        variant: "destructive",
      });
    }
  }



  async function deleteAttendance(childId) {
    if (!selectedSessionId) return;

    const isAllGroups = attendanceGroupFilter === "all";
    const canUseAllGroups = isSuperAdmin || isAdmin;

    if (!attendanceGroupFilter || (isAllGroups && !canUseAllGroups)) {
      // Ohne Gruppe oder bei "Alle" für Trainer/Mitarbeiter keine Löschung ermöglichen
      return;
    }

    const effectiveGroupId = isAllGroups ? null : attendanceGroupFilter;

    try {
      await axios.delete(`${API_BASE}/api/attendance`, {
        params: {
          session_id: selectedSessionId,
          child_id: childId,
          group_id: effectiveGroupId,
        },
        ...authHeaders(),
      });
    } catch (e) {
      console.error(e);
      // Fehler hier nur loggen, da der UI-Status bereits zurückgesetzt ist
    }
  }

  function setAttendanceStatus(childId, status) {
    setAttendance((prev) => {
      const current = prev[childId] || { status: "", comment: "" };
      const nextStatus = current.status === status ? "" : status;
      const next = { ...current, status: nextStatus };
      // Optimistisch im State setzen
      const updated = { ...prev, [childId]: next };
      // Persistenz im Backend
      if (nextStatus) {
        saveSingleAttendance(childId, { status: nextStatus, comment: next.comment });
      } else {
        // Bei Leeren des Status Eintrag komplett löschen
        deleteAttendance(childId);
      }
      return updated;
    });
  }

  function setAttendanceComment(childId, comment) {
    setAttendance((prev) => ({
      ...prev,
      [childId]: { ...(prev[childId] || { status: "" }), comment },
    }));
  }

  async function handleSaveAttendance() {
    if (!selectedSessionId) {
      toast({
        title: "Bitte zuerst einen Kurstermin (Samstag) wählen.",
        variant: "destructive",
      });
      return;
    }

    const isAllGroups = attendanceGroupFilter === "all";
    const canUseAllGroups = isSuperAdmin || isAdmin;

    if (!attendanceGroupFilter || (isAllGroups && !canUseAllGroups)) {
      toast({
        title: "Bitte zuerst eine konkrete Gruppe für die Anwesenheit auswählen.",
        variant: "destructive",
      });
      return;
    }

    const effectiveGroupId = isAllGroups ? null : attendanceGroupFilter;

    const entries = attendanceChildren.map((c) => ({
      child_id: c.id,
      status: attendance[c.id]?.status || "fehlt",
      comment: attendance[c.id]?.comment || null,
    }));

    try {
      await axios.post(
        `${API_BASE}/api/attendance/bulk`,
        {
          session_id: selectedSessionId,
          group_id: effectiveGroupId,
          entries,
        },
        authHeaders(),
      );
      fetchAttendanceForSession(selectedSessionId, attendanceGroupFilter);
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Speichern der Anwesenheit",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }

  async function handleLoadStats() {
    try {
      const paramsRange = {};
      if (statsFrom) paramsRange.from_date = statsFrom;
      if (statsTo) paramsRange.to_date = statsTo;

      if (statsGroupFilter === "all") {
        if (!statsChildId) {
          toast({ title: "Bitte zuerst ein Kind auswählen", variant: "destructive" });
          return;
        }

        // Einzelkind-Matrix: alle Termine im Zeitraum + Status pro Termin
        const resSessions = await axios.get(`${API_BASE}/api/sessions`, {
          params: paramsRange,
          ...authHeaders(),
        });
        let sessionsInRange = resSessions.data || [];
        if (sessionsInRange.length === 0) {
          toast({ title: "Keine Kurstermine im Zeitraum", variant: "destructive" });
          setChildStatsMatrix({ sessions: [], child: selectedStatsChild, data: {} });
          setStats(null);
          setGroupStatsMatrix(null);
          return;
        }

        const paramsAtt = { child_id: statsChildId };
        if (statsFrom) paramsAtt.from_date = statsFrom;
        if (statsTo) paramsAtt.to_date = statsTo;

        const resAtt = await axios.get(`${API_BASE}/api/attendance`, {
          params: paramsAtt,
          ...authHeaders(),
        });

        const attendanceItems = resAtt.data || [];
        // Nur Termine berücksichtigen, für die es Anwesenheitseinträge gibt
        const sessionIdsWithAttendance = new Set(attendanceItems.map((a) => a.session_id));
        const filteredSessions = sessionsInRange.filter((s) => sessionIdsWithAttendance.has(s.id));

        const dataMap = {};
        attendanceItems.forEach((a) => {
          dataMap[a.session_id] = { status: a.status, comment: a.comment || "" };
        });

        setChildStatsMatrix({ sessions: filteredSessions, child: selectedStatsChild, data: dataMap });
        setStats(null);
        setGroupStatsMatrix(null);
      } else {
        // Gruppenauswertung mit Matrix
        const groupChildren = statsChildren;

        if (!groupChildren || groupChildren.length === 0) {
          toast({ title: "Keine Kinder in dieser Gruppe", variant: "destructive" });
          setGroupStatsMatrix({ sessions: [], children: [], data: {} });
          setStats(null);
          return;
        }

        const resSessions = await axios.get(`${API_BASE}/api/sessions`, {
          params: paramsRange,
          ...authHeaders(),
        });
        let sessionsInRange = resSessions.data || [];
        if (sessionsInRange.length === 0) {
          toast({ title: "Keine Kurstermine im Zeitraum", variant: "destructive" });
          setGroupStatsMatrix({ sessions: [], children: groupChildren, data: {} });
          setStats(null);
          return;
        }

        // Sessions nach Datum sortieren (älteste zuerst)
        sessionsInRange = [...sessionsInRange].sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0));

        const paramsAtt = { group_id: statsGroupFilter };
        if (statsFrom) paramsAtt.from_date = statsFrom;
        if (statsTo) paramsAtt.to_date = statsTo;

        const resAtt = await axios.get(`${API_BASE}/api/attendance`, {
          params: paramsAtt,
          ...authHeaders(),
        });

        const attendanceItems = resAtt.data || [];
        const dataMap = {};
        attendanceItems.forEach((a) => {
          if (!dataMap[a.child_id]) dataMap[a.child_id] = {};
          dataMap[a.child_id][a.session_id] = a.status;
        });

        setGroupStatsMatrix({ sessions: sessionsInRange, children: groupChildren, data: dataMap });

        // Startseite der Matrix so wählen, dass der aktuelle oder nächste Samstag zuerst sichtbar ist
        const todayISO = new Date().toISOString().slice(0, 10);
        const effectiveSessions = showOnlyAttendedTerms
          ? sessionsInRange.filter((s) =>
              groupChildren.some((c) => dataMap?.[c.id]?.[s.id]),
            )
          : sessionsInRange;

        if (effectiveSessions.length > 0) {
          let startIndex = effectiveSessions.findIndex((s) => s.date >= todayISO);
          if (startIndex === -1) {
            // alle Termine liegen in der Vergangenheit → letzte Seite anzeigen
            startIndex = Math.max(effectiveSessions.length - 1, 0);
          }
          const initialPage = Math.floor(startIndex / 10);
          setGroupMatrixPage(initialPage);
        } else {
          setGroupMatrixPage(0);
        }

        setStats(null);
      }
    } catch (e) {
      console.error(e);
      toast({
        title: "Fehler beim Laden der Auswertung",
        description: "Bitte später erneut versuchen.",
        variant: "destructive",
      });
    }
  }

  function handleExportCsv() {
    const params = new URLSearchParams();
    if (exportFrom) params.append("from_date", exportFrom);
    if (exportTo) params.append("to_date", exportTo);
    const url = `${API_BASE}/api/attendance/export${params.toString() ? `?${params.toString()}` : ""}`;
    // Für Auth nutzen wir besser einen Download direkt mit axios, statt Header in der URL zu kodieren.
    axios
      .get(url, {
        responseType: "blob",
        ...(authToken
          ? {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          : {}),
      })
      .then((res) => {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = "anwesenheit.csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((e) => {
        console.error(e);
        toast({ title: "Export fehlgeschlagen", description: "Bitte später erneut versuchen.", variant: "destructive" });
      });
  }

  function handleTrainerEmailChange(value) {
    setTrainerEmail(value);
    window.localStorage.setItem("trainer_email", value);
  }

  async function fetchGroups(retryCount = 0) {
    setGroupsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/groups`, authHeaders());
      let data = res.data || [];
      // Trainer sieht nur seine eigenen Gruppen
      if (currentUser?.role === "trainer" && Array.isArray(currentUser.allowed_group_ids) && currentUser.allowed_group_ids.length > 0) {
        data = data.filter((g) => currentUser.allowed_group_ids.includes(g.id));
      }
      setGroups(data);
      setGroupsLoading(false);
    } catch (e) {
      console.error("fetchGroups Fehler:", e);
      // Retry-Logik: bis zu 3 Versuche bei Netzwerkfehlern
      if (retryCount < 3) {
        console.log(`Retry fetchGroups (${retryCount + 1}/3)...`);
        setTimeout(() => fetchGroups(retryCount + 1), 1000 * (retryCount + 1));
      } else {
        setGroupsLoading(false);
        toast({ title: "Fehler beim Laden der Gruppen", description: "Bitte Seite neu laden", variant: "destructive" });
      }
    }
  }

  async function handleGroupSubmit(e) {
    e.preventDefault();
    const payload = {
      name: groupForm.name,
      trainer: groupForm.trainer,
      weekday: groupForm.weekday,
      start_time: groupForm.start_time,
      end_time: groupForm.end_time,
      color: groupForm.color || null,
      order: groupForm.order ? parseInt(groupForm.order, 10) : null,
    };
    try {
      if (groupForm.id) {
        await axios.put(`${API_BASE}/api/groups/${groupForm.id}`, payload, authHeaders());
        toast({ title: "Gruppe aktualisiert" });
      } else {
        await axios.post(`${API_BASE}/api/groups`, payload, authHeaders());
        toast({ title: "Gruppe angelegt" });
      }
      setGroupForm({ id: null, name: "", trainer: "", weekday: "Sa.", start_time: "", end_time: "" });
      fetchGroups();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Bitte Eingaben prüfen oder später erneut versuchen.";
      toast({ title: "Fehler beim Speichern der Gruppe", description: msg, variant: "destructive" });
    }
  }

  function handleGroupEdit(g) {
    setGroupForm({
      id: g.id,
      name: g.name,
      trainer: g.trainer,
      weekday: g.weekday,
      start_time: g.start_time,
      end_time: g.end_time,
      color: g.color || "",
      order: (g.order ?? "").toString(),
    });
  }

  async function handleGroupDelete(id) {
    if (!window.confirm("Diese Gruppe wirklich löschen?")) return;
    try {
      await axios.delete(`${API_BASE}/api/groups/${id}`, authHeaders());
      toast({ title: "Gruppe gelöscht" });
      fetchGroups();
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Löschen der Gruppe", variant: "destructive" });
    }
  }

  async function handleForgotPassword(emailAddress) {
    try {
      await axios.post(`${API_BASE}/api/auth/password-reset-request`, { 
        email: emailAddress,
        origin: window.location.origin  // Aktuelle URL für Reset-Link
      });
      toast({
        title: "Falls die E-Mail existiert...",
        description: "...haben wir dir einen Link zum Zurücksetzen geschickt.",
      });
    } catch (e) {
      console.error(e);
      // Immer gleiche Meldung, um keine Informationen über existierende Benutzer preiszugeben
      toast({
        title: "Falls die E-Mail existiert...",
        description: "...haben wir dir einen Link zum Zurücksetzen geschickt.",
      });
    }
  }

  function applyMailEditorStylesToSelection() {
    // Alte execCommand-basierte Styles werden nicht mehr benötigt
    return;
  }

  async function handleLogin({ email, password }) {
    try {
      const res = await axios.post(`${API_BASE}/api/auth/login`, { email, password, device_id: deviceId });
      
      // Prüfen ob 2FA erforderlich ist (Status 202)
      if (res.status === 202 && res.data.requires_2fa) {
        setTwoFactorPending({
          user_id: res.data.user_id,
          email: res.data.email
        });
        toast({ title: "Verifikationscode gesendet", description: "Bitte prüfen Sie Ihre E-Mails." });
        return;
      }
      
      // Normaler Login-Erfolg
      completeLogin(res.data);
    } catch (e) {
      console.error(e);
      // Auch bei 202 im Error-Handler (axios behandelt 202 manchmal als Error)
      if (e.response?.status === 202 && e.response?.data?.requires_2fa) {
        setTwoFactorPending({
          user_id: e.response.data.user_id,
          email: e.response.data.email
        });
        toast({ title: "Verifikationscode gesendet", description: "Bitte prüfen Sie Ihre E-Mails." });
        return;
      }
      const msg = e?.response?.data?.detail || "Bitte E-Mail und Passwort prüfen.";
      toast({ title: "Login fehlgeschlagen", description: msg, variant: "destructive" });
    }
  }

  function completeLogin(data) {
    const token = data.access_token;
    const mustChange = !!data.must_change_password;
    setAuthToken(token);
    window.localStorage.setItem("auth_token", token);
    setCurrentUser({
      id: data.id,
      email: data.email,
      full_name: data.full_name,
      role: data.role,
      allowed_group_ids: data.allowed_group_ids || [],
      must_change_password: mustChange,
      permissions: data.permissions || {},
    });
    setForcePasswordChange(mustChange);
    setTwoFactorPending(null);
    
    // Backup-Code Warnung - NUR für Super-Admins relevant (2FA ist nur für Super-Admins)
    if (data.role === "superadmin" && data.backup_codes_remaining !== undefined && data.backup_codes_remaining <= 3) {
      setTimeout(() => {
        toast({ 
          title: `Nur noch ${data.backup_codes_remaining} Notfall-Code(s)!`, 
          description: "Bitte generieren Sie neue Codes unter Einstellungen → Profil.",
          variant: data.backup_codes_remaining === 0 ? "destructive" : "default"
        });
      }, 1000);
    }
    
    // Startseite ist immer "Kinder" für alle Rollen (außer Mobile - dort wird es in ModernNavigation behandelt)
    setSelectedTab("children");
    
    window.localStorage.setItem(
      "current_user",
      JSON.stringify({
        id: data.id,
        email: data.email,
        full_name: data.full_name,
        role: data.role,
        allowed_group_ids: data.allowed_group_ids || [],
        must_change_password: mustChange,
        permissions: data.permissions || {},
      }),
    );
  }

  function handle2FASuccess(data) {
    completeLogin(data);
  }

  function handle2FACancel() {
    setTwoFactorPending(null);
  }

  function handleLogout() {
    setAuthToken("");
    setCurrentUser(null);
    setForcePasswordChange(false);
    window.localStorage.removeItem("auth_token");
    // URL-Parameter bereinigen beim Logout
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  // Auto-Logout: Session-Einstellungen laden und Inaktivitäts-Timer
  useEffect(() => {
    // Session-Einstellungen laden
    const loadSessionSettings = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/session-settings`);
        setSessionSettings(res.data);
      } catch (err) {
        console.error("Session-Einstellungen konnten nicht geladen werden:", err);
      }
    };
    loadSessionSettings();

    // Event-Listener für Session-Settings-Updates
    const handleSettingsUpdate = (e) => {
      if (e.detail) setSessionSettings(e.detail);
    };
    window.addEventListener('session-settings-updated', handleSettingsUpdate);
    return () => window.removeEventListener('session-settings-updated', handleSettingsUpdate);
  }, []);

  // Auto-Logout: Inaktivitäts-Überwachung
  useEffect(() => {
    if (!isLoggedIn || !sessionSettings.auto_logout_enabled) return;

    const timeoutMs = sessionSettings.auto_logout_minutes * 60 * 1000;

    // Aktivität registrieren
    const resetActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Inaktivität prüfen
    const checkInactivity = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= timeoutMs) {
        handleLogout();
        toast({
          title: "Automatisch abgemeldet",
          description: `Sie wurden nach ${sessionSettings.auto_logout_minutes} Minuten Inaktivität abgemeldet.`,
          variant: "default",
        });
      }
    };

    // Event-Listener für Benutzeraktivität
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, resetActivity, { passive: true }));

    // Alle 30 Sekunden prüfen
    const intervalId = setInterval(checkInactivity, 30000);

    // Cleanup
    return () => {
      events.forEach(event => window.removeEventListener(event, resetActivity));
      clearInterval(intervalId);
    };
  }, [isLoggedIn, sessionSettings.auto_logout_enabled, sessionSettings.auto_logout_minutes]);

  function insertEmailPlaceholder(placeholder) {
    const token = `{{${placeholder}}}`;
    // Stabile Variante: immer ans Ende anhängen
    setEmailBody((prev) => (prev || "") + token);
  }

  function applyEmailFormat() {
    // Alte execCommand-basierte Formatierung nicht mehr nötig, wird komplett über TipTap gesteuert
    return;
  }


  function handleEmailTemplateChange({ subject, body }) {
    if (typeof subject === "string") {
      setEmailSubject(subject);
    }
    if (typeof body === "string") {
      setEmailBody(body);
      setInitialEmailBodyLoaded(true);
    }
  }

  async function handleSendGroupEmail() {
    if (!emailSubject || !emailBody) {
      toast({
        title: "Bitte Betreff und Text ausfüllen",
        variant: "destructive",
      });
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/api/email/group`,
        {
          subject: emailSubject,
          body: emailBody,
          group_ids: selectedEmailGroupIds.length > 0 ? selectedEmailGroupIds : null,
          filter_mode: emailGroupFilterMode,
        },
        authHeaders(),
      );
      toast({
        title: "E-Mails versendet",
        description: `An ${res.data.sent_count} Empfänger gesendet, ${res.data.skipped_no_email} ohne E-Mail-Adresse übersprungen.`,
      });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "E-Mails konnten nicht versendet werden.";
      toast({ title: "Fehler beim Versenden", description: msg, variant: "destructive" });
    }


  }

  function formatDateISOToGerman(d) {
    if (!d) return "";
    try {
      return format(parseISO(d), "dd.MM.yyyy", { locale: de });
    } catch {
      return d;
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {showScrollTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-4 left-12 z-40 inline-flex h-10 w-10 items-center justify-center rounded-full text-white shadow-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-50"
          style={{ backgroundColor: mailEditorColors[2] || "#0f766e" }}
          aria-label="Nach oben scrollen"
          data-testid="scroll-to-top-button"
        >
          ↑
        </button>
      )}
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-4 sm:px-6 sm:py-6">
        <header className="flex flex-col gap-2 border-b border-slate-200 pb-4">
          <div className="flex items-baseline justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-1">
                <img
                  src="/fitmacher_logo.png"
                  alt="Fitmacher Schwimmen Logo"
                  className="h-20 w-auto"
                />
                <p className="max-w-2xl text-sm text-slate-600" data-testid="app-subtitle">
                  Admin-Tool für die Schwimmkurse
                </p>
              </div>
            </div>
            {isLoggedIn && currentUser && (
              <div className="flex flex-col items-end text-xs text-slate-600" data-testid="user-info">
                <span>Eingeloggt als {currentUser.full_name || currentUser.email}</span>
                <span className="capitalize">Rolle: {currentUser.role}</span>
                <div className="mt-1 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(true)}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    data-testid="account-settings-button"
                  >
                    Passwort ändern
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    data-testid="logout-button"
                  >
                    Abmelden
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>


        {!isLoggedIn && !twoFactorPending && (
          <div className="mt-4 grid gap-4 md:grid-cols-2" data-testid="login-forms">
            <Card className="border-slate-200 bg-white/90 w-full" data-testid="login-card">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-slate-900">Anmeldung</CardTitle>
              </CardHeader>
              <CardContent>
                <LoginForm onLogin={handleLogin} onForgotPassword={handleForgotPassword} />
              </CardContent>
            </Card>


          </div>
        )}

        {/* 2FA Verifikation */}
        {!isLoggedIn && twoFactorPending && (
          <TwoFactorVerification
            userId={twoFactorPending.user_id}
            email={twoFactorPending.email}
            deviceId={deviceId}
            onSuccess={handle2FASuccess}
            onCancel={handle2FACancel}
          />
        )}

        {isLoggedIn && (
        <>

        {forcePasswordChange && currentUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl border border-slate-200" data-testid="force-password-change-modal">
              <h2 className="mb-2 text-base font-semibold text-slate-900">Passwort aktualisieren</h2>
              <p className="mb-3 text-sm text-slate-700">
                Dies ist deine erste Anmeldung mit einem Initial-Passwort. Bitte vergebe jetzt ein neues, sicheres Passwort,
                bevor du die App weiter verwendest.
              </p>
              <PasswordChangeForm
                authToken={authToken}
                onSuccess={() => {
                  toast({ title: "Passwort aktualisiert" });
                  setForcePasswordChange(false);
                  setCurrentUser((prev) => (prev ? { ...prev, must_change_password: false } : prev));
                  const stored = window.localStorage.getItem("current_user");
                  if (stored) {
                    try {
                      const parsed = JSON.parse(stored);
                      parsed.must_change_password = false;
                      window.localStorage.setItem("current_user", JSON.stringify(parsed));
                    } catch {
                      // ignorieren
                    }
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Passwort ändern Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowPasswordModal(false)}>
            <div 
              className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl border border-slate-200" 
              onClick={(e) => e.stopPropagation()}
              data-testid="password-change-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Passwort ändern</h2>
                <button 
                  onClick={() => setShowPasswordModal(false)}
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <PasswordChangeForm
                authToken={authToken}
                onSuccess={() => {
                  toast({ title: "Passwort erfolgreich geändert" });
                  setShowPasswordModal(false);
                }}
              />
            </div>
          </div>
        )}

          {/* Permanenter File-Input für Foto-Upload (wichtig für mobile Geräte) */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoInputChange}
            className="hidden"
            aria-hidden="true"
          />

          <Dialog open={cropDialogOpen} onOpenChange={(open) => {
            if (!open) {
              // Cleanup wenn Dialog geschlossen wird
              if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
              setCropImageSrc(null);
              setCropForChildId(null);
              setCroppedAreaPixels(null);
              setCropOnSavedCallback(null);
              setCrop({ x: 0, y: 0 });
              setZoom(1);
            }
            setCropDialogOpen(open);
          }}>
            <DialogContent className="sm:max-w-[400px]">
              <DialogHeader>
                <DialogTitle>Profilfoto zuschneiden</DialogTitle>
              </DialogHeader>
              <div className="mt-2 flex flex-col gap-3">
                <div className="relative h-64 w-full overflow-hidden rounded-xl bg-slate-900/80">
                  {cropImageSrc && (
                    <Cropper
                      image={cropImageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">Zoom</span>
                  <Slider
                    value={[zoom]}
                    min={1}
                    max={3}
                    step={0.1}
                    onValueChange={(val) => setZoom(val[0] || 1)}
                    className="flex-1"
                  />
                </div>
              </div>
              <DialogFooter className="mt-4 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
                    setCropImageSrc(null);
                    setCropForChildId(null);
                    setCroppedAreaPixels(null);
                    setCropDialogOpen(false);
                    setCropOnSavedCallback(null);
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                  }}
                >
                  Abbrechen
                </Button>
                <Button type="button" size="sm" onClick={handleConfirmCrop}>
                  Übernehmen
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        <Tabs
          value={selectedTab}
          onValueChange={setSelectedTab}
          className="flex flex-col"
          data-testid="main-tabs"
        >
          {/* Moderne Navigation */}
          <ModernNavigation
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            settingsTab={settingsTab}
            setSettingsTab={setSettingsTab}
            userRole={currentUser?.role || "mitarbeiter"}
            deviceType={deviceType}
            permissions={currentUser?.permissions || {}}
          />

          <TabsContent value="children" data-testid="tab-content-children" className="mt-4 flex flex-col gap-4">
            <ChildrenTabModern
                children={sortedChildren}
                archivedChildren={archivedChildren}
                groups={groups}
                sessions={sessions}
                attendance={attendance}
                authToken={authToken}
                authHeaders={authHeaders}
                toast={toast}
                deviceType={deviceType}
                isSuperAdmin={isSuperAdmin}
                isAdmin={isAdmin}
                formatDateISOToGerman={formatDateISOToGerman}
                fetchChildren={fetchChildren}
                initialSelectedChildId={selectedChildForProfile?.id}
                onChildSelect={(child) => setSelectedChildForProfile(child)}
                onEditChild={(child) => {
                  handleChildEdit(child);
                  setEditingChild(child || null);
                }}
                onArchiveChild={async (child) => {
                  if (!window.confirm(`"${child.first_name} ${child.last_name}" wirklich archivieren?`)) return false;
                  try {
                    console.log("Archiviere Kind:", child.id);
                    await axios.post(`${API_BASE}/api/children/${child.id}/archive`, {}, authHeaders());
                    console.log("Archivierung erfolgreich");
                    toast({ title: "Kind archiviert", description: `${child.first_name} ${child.last_name} wurde ins Archiv verschoben.` });
                    await fetchChildren();
                    return true; // Erfolg signalisieren
                  } catch (err) {
                    console.error("Archivierungsfehler:", err);
                    toast({ title: "Fehler beim Archivieren", description: err?.response?.data?.detail || "Bitte später erneut versuchen.", variant: "destructive" });
                    return false;
                  }
                }}
                onRestoreChild={async (child, groupId) => {
                  try {
                    await axios.post(`${API_BASE}/api/children/${child.id}/restore`, { group_id: groupId }, authHeaders());
                    const group = groups.find(g => g.id === groupId);
                    toast({ title: `${child.first_name} ${child.last_name} wurde wiederhergestellt und der Gruppe "${group?.name || groupId}" zugewiesen` });
                    fetchChildren();
                  } catch (err) {
                    console.error(err);
                    toast({ title: "Fehler beim Wiederherstellen", variant: "destructive" });
                  }
                }}
                onDeleteArchivedChild={handleDeleteArchivedChild}
                onPromoteGroup={async (child, navigateToNextCallback) => {
                  await handlePromoteChildGroup(child.id);
                  if (navigateToNextCallback) navigateToNextCallback();
                }}
                onDemoteGroup={async (child, navigateToNextCallback) => {
                  await handleDemoteChildGroup(child.id);
                  if (navigateToNextCallback) navigateToNextCallback();
                }}
                onChangeGroup={async (child, targetGroupId, navigateToNextCallback) => {
                  await handleChangeChildGroup(child.id, targetGroupId);
                  if (navigateToNextCallback) navigateToNextCallback();
                }}
                onChildUpdate={handleChildUpdate}
                onPhotoUpload={(child, onSavedCallback) => {
                  // Verwende permanenten File-Input für bessere Mobilkompatibilität
                  triggerPhotoUpload(child.id, onSavedCallback);
                }}
                onPhotoDelete={async (child, onDeletedCallback) => {
                  if (!window.confirm("Möchten Sie das Profilfoto wirklich löschen?")) return;
                  try {
                    const response = await axios.delete(`${API_BASE}/api/children/${child.id}/photo`, authHeaders());
                    toast({ title: "Foto gelöscht" });
                    if (response?.data) {
                      handleChildUpdate(response.data);
                      // Callback für lokalen State aufrufen
                      if (onDeletedCallback) {
                        onDeletedCallback(response.data);
                      }
                    }
                  } catch (err) {
                    console.error(err);
                    toast({ title: "Fehler beim Löschen", variant: "destructive" });
                  }
                }}
                permissions={currentUser?.permissions || {}}
              />

          </TabsContent>

          <TabsContent value="import" data-testid="tab-content-import" className="mt-4 flex flex-col gap-4">
            <Card className="border-slate-200 bg-white/90 w-full" data-testid="import-card">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-slate-900">Kinder importieren</CardTitle>
                <CardDescription className="text-sm text-slate-600">
                  Kinder aus einer Excel-Datei (XLSX) importieren. Erwartete Spalten: Vorname, Nachname, E-Mail, Telefon, Geburtsdatum.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-slate-600">
                  Hinweis: In dieser ersten Version werden immer neue Kinder angelegt. Doppelte Einträge werden noch nicht automatisch erkannt.
                </p>
                <ImportChildrenForm
                  apiBase={API_BASE}
                  authHeaders={authHeaders}
                  groups={groups}
                  onImported={() => {
                    fetchChildren();
                    fetchGroups();
                    fetchImports();
                  }}
                />
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white/90 w-full" data-testid="import-history-card">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-slate-900">Import-Historie</CardTitle>
                <CardDescription className="text-sm text-slate-600">
                  Übersicht der letzten Importe (jeweils 10 Einträge pro Seite).
                </CardDescription>
              </CardHeader>
              <CardContent>
                {imports.length === 0 ? (
                  <p className="text-xs text-slate-500">Es wurden noch keine Importe durchgeführt.</p>
                ) : (
                  <ImportHistoryTable
                    imports={imports}
                    groups={groups}
                    formatDateISOToGerman={formatDateISOToGerman}
                    onRefresh={fetchImports}
                    apiBase={API_BASE}
                    authHeaders={authHeaders}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="attendance" data-testid="tab-content-attendance" className="mt-4 flex flex-col gap-4">
            <AttendanceTabModern
                children={children}
                groups={groups}
                groupsLoading={groupsLoading}
                sessions={sessions}
                attendance={attendance}
                setAttendance={setAttendance}
                selectedSessionId={selectedSessionId}
                setSelectedSessionId={setSelectedSessionId}
                authToken={authToken}
                authHeaders={authHeaders}
                toast={toast}
                deviceType={deviceType}
                isSuperAdmin={isSuperAdmin}
                isAdmin={isAdmin}
                isTrainer={isTrainer}
                currentUser={currentUser}
                formatDateISOToGerman={formatDateISOToGerman}
                fetchAttendanceForSession={fetchAttendanceForSession}
                generateOfflinePDF={handleDownloadOfflineAttendancePdf}
              />
          </TabsContent>

          <TabsContent value="stats" data-testid="tab-content-stats" className="mt-4 flex flex-col gap-4">
            <EvaluationTabModern
                children={children}
                groups={groups}
                authHeaders={authHeaders}
                authToken={authToken}
                toast={toast}
                formatDateISOToGerman={formatDateISOToGerman}
              />
          </TabsContent>

          <TabsContent value="course-sessions" data-testid="tab-content-course-sessions" className="mt-4 flex flex-col gap-4">
            <CourseSessionsTabModern
                sessions={sessions}
                authHeaders={authHeaders}
                toast={toast}
                fetchSessions={fetchSessions}
                formatDateISOToGerman={formatDateISOToGerman}
                isSuperAdmin={isSuperAdmin}
                isAdmin={isAdmin}
              />

          </TabsContent>

          {/* KURSBUCHUNG TAB */}
          <TabsContent value="courses" data-testid="tab-content-courses" className="mt-4 flex flex-col gap-4">
            <CoursesTabModern
              authToken={authToken}
              groups={groups}
              onChildrenImported={fetchChildren}
            />
          </TabsContent>

          {/* GRUPPEN TAB */}
          <TabsContent value="groups" data-testid="tab-content-groups" className="mt-4 flex flex-col gap-4">
            <GroupsTabModern
              groups={groups}
              children={children}
              authHeaders={authHeaders}
              toast={toast}
              fetchGroups={fetchGroups}
              isSuperAdmin={isSuperAdmin}
            />
          </TabsContent>

          {/* E-MAIL TAB */}
          <TabsContent value="email" data-testid="tab-content-email" className="mt-4 flex flex-col gap-4">
            <EmailTabModern
              children={children}
              groups={groups}
              authHeaders={authHeaders}
              authToken={authToken}
              toast={toast}
              mailEditorColors={mailEditorColors}
              permissions={currentUser?.permissions || {}}
              isSuperAdmin={isSuperAdmin}
              onSendComplete={() => {
                // Nach erfolgreichem E-Mail-Versand: Sendeprotokolle aktualisieren
                if (emailLogsRef.current) {
                  emailLogsRef.current.refreshLogs();
                }
              }}
            />
            <EmailLogsCard ref={emailLogsRef} authToken={authToken} isSuperAdmin={isSuperAdmin} isAdmin={isAdmin} />
          </TabsContent>

          {/* TICKETS TAB */}
          <TabsContent value="tickets" data-testid="tab-content-tickets" className="mt-4">
            <TicketsTabModern
              authHeaders={authHeaders}
              isSuperAdmin={isSuperAdmin}
              currentUser={currentUser}
            />
          </TabsContent>

          {/* HILFE TAB */}
          <TabsContent value="help" data-testid="tab-content-help" className="mt-4">
            <HelpTab authToken={authToken} />
          </TabsContent>

          {/* EINSTELLUNGEN TAB (account) */}
          {(isSuperAdmin || isAdmin) && (
          <TabsContent value="account" data-testid="tab-content-account" className="mt-4 flex flex-col gap-4">
            {/* Bereich: Konto (Eigenes Passwort ändern) */}
            {settingsTab === "account" && (
              <>
                <Card className="border-slate-200 bg-white/90 w-full" data-testid="account-password-card">
                  <CardHeader>
                    <CardTitle className="text-base font-semibold text-slate-900">Eigenes Passwort ändern</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PasswordChangeForm authToken={authToken} onSuccess={() => toast({ title: "Passwort aktualisiert" })} />
                  </CardContent>
                </Card>
                
                {/* 2FA Backup-Codes - nur für Super-Admin */}
                {isSuperAdmin && (
                  <Card className="border-slate-200 bg-white/90 w-full" data-testid="backup-codes-card">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-slate-900">Notfall-Codes (2FA)</CardTitle>
                      <CardDescription className="text-sm text-slate-500">
                        Backup-Codes für den Fall, dass Sie keinen E-Mail-Zugang haben
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <BackupCodesManager authToken={authToken} />
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Bereich: Benutzerverwaltung - Modern */}
            {settingsTab === "users" && (
              <UsersTabModern authToken={authToken} groups={groups} isSuperAdmin={isSuperAdmin} />
            )}

            {/* Bereich: Berechtigungen (nur Super-Admin) */}
            {settingsTab === "permissions" && isSuperAdmin && (
              <RolePermissionsMatrix authToken={authToken} />
            )}

            {/* Bereich: Mailserver & System - Modern */}
            {(settingsTab === "mail" || settingsTab === "system") && (
              <SystemSettingsTabModern
                authToken={authToken}
                groups={groups}
                imports={imports}
                fetchImports={fetchImports}
                fetchChildren={fetchChildren}
                isSuperAdmin={isSuperAdmin}
              />
            )}
          </TabsContent>
          )}

          {/* Demo-Tab für Design-Varianten */}
          {isSuperAdmin && (
          <TabsContent value="demo" data-testid="tab-content-demo" className="mt-4 flex flex-col gap-4">
            <DemoTab 
              children={children}
              groups={groups}
              sessions={sessions}
              attendance={attendance}
              authToken={authToken}
              API_BASE={API_BASE}
              onSelectChild={(child) => {
                setSelectedChildForProfile(child);
                setSelectedTab("children");
              }}
              deviceType={deviceType}
            />
          </TabsContent>
          )}
        </Tabs>
        </>
        )}
      </div>
      <Toaster />
      
      {/* Kind-Bearbeitungs-Dialog */}
      <ChildFormDialog
        open={showChildFormCard}
        onOpenChange={(open) => {
          setShowChildFormCard(open);
          if (!open) setEditingChild(null);
        }}
        child={editingChild}
        groups={groups}
        isSaving={savingChild}
        onSave={async (formData) => {
          setSavingChild(true);
          try {
            // Datumsfelder verarbeiten
            let birth_date_iso = null;
            if (formData.birth_date) {
              const raw = formData.birth_date.trim();
              if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                birth_date_iso = raw;
              } else if (/^\d{2}\.\d{2}\.\d{4}$/.test(raw)) {
                const [d, m, y] = raw.split(".");
                birth_date_iso = `${y}-${m}-${d}`;
              }
            }
            
            let pause_until_iso = null;
            if (formData.pause_until) {
              const raw = formData.pause_until.trim();
              if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                pause_until_iso = raw;
              } else if (/^\d{2}\.\d{2}\.\d{4}$/.test(raw)) {
                const [d, m, y] = raw.split(".");
                pause_until_iso = `${y}-${m}-${d}`;
              }
            }
            
            // Geschlecht zurück ins Backend-Format konvertieren
            const genderMap = { 'm': 'junge', 'w': 'mädchen' };
            const genderValue = formData.gender ? (genderMap[formData.gender] || formData.gender) : null;
            
            const payload = {
              first_name: formData.first_name,
              last_name: formData.last_name,
              birth_date: birth_date_iso,
              phone: formData.phone1 || null,
              phone2: formData.phone2 || null,
              phone3: formData.phone3 || null,
              email: formData.email1 || null,
              secondary_email: formData.email2 || null,
              remark: formData.remarks || null,
              notes: formData.notes || null,
              mnr: formData.mnr || null,
              swim_start: formData.swimming_since || null,
              gender: genderValue,
              group_id: formData.group_id || null,
              pause_until: pause_until_iso,
              membership_end_date: formData.membership_end_date || null,
            };
            
            if (formData.id) {
              // Bestehendes Kind aktualisieren
              await axios.put(`${API_BASE}/api/children/${formData.id}`, payload, authHeaders());
              toast({ title: "Kind aktualisiert" });
              setShowChildFormCard(false);
              setEditingChild(null);
              setSelectedChildForProfile((prev) => {
                if (prev && prev.id === formData.id) {
                  return { ...prev, ...payload, id: formData.id };
                }
                const base = children.find((c) => c.id === formData.id) || {};
                return { ...base, ...payload, id: formData.id };
              });
            } else {
              // Neues Kind anlegen
              await axios.post(`${API_BASE}/api/children`, payload, authHeaders());
              toast({ title: "Kind angelegt" });
              setShowChildFormCard(false);
              setEditingChild(null);
            }
            fetchChildren().catch(() => {}); // Fehler beim Neuladen nicht als Speicherfehler anzeigen
          } catch (err) {
            console.error(err);
            toast({ title: "Fehler beim Speichern des Kindes", variant: "destructive" });
          }
          setSavingChild(false);
        }}
      />
      
      {/* Footer mit Copyright */}
      <AppFooter />
    </div>
  );
}



export default App;
